import * as classnames from 'classnames'
import * as React from 'react'
import startCase from 'lodash-es/startCase'
import tail from 'lodash-es/tail'
import { marketApi } from '@envato/foundation-types'
import ga from '../../../../common/utils/ga'

import SearchLinkContainer from '../../../containers/searchLinkContainer'
import SVGSpriteWrapper from '../../svgSpriteWrapper'
import { TabbedTableIcons } from '../../icons/categoryPage'
import { Tabs, Tab } from '../../tabs'
import { config } from '../../../../common/config'
import configs from '../../../../config/sites/categories'

import { ICategoryPageBlockComponent } from '../../../../typings/pageBlocks'

import * as styles from './textCategoriesBlock.module.css'

const MAX_ITEMS_IN_ROW = 4

interface IProps extends ICategoryPageBlockComponent {
  data?: any
}

interface ITextCategoriesBlockProps extends ICategoryPageBlockComponent {
  data?: any
}

const chevronIcon = (
  <span className={styles.tabbedTableChevronIcon}>
    {TabbedTableIcons.chevron_right}
  </span>
)

const reportClick = (label: string) => {
  ga('send', {
    hitType: 'event',
    eventCategory: 'Block interaction;CategoryText',
    eventAction: 'click;link',
    eventLabel: label
  })
}

const renderLink = (className: string, appendix?: JSX.Element) => (
  link: any,
  linkIndex: number
) => (
  <SearchLinkContainer
    key={linkIndex}
    className={className}
    locationParams={link.urlSearchParams}
    onClick={() => reportClick(link.name)}
  >
    <span className={styles.linkInner}>
      {link.name}
      {appendix}
    </span>
  </SearchLinkContainer>
)

interface IViewHideButton {
  isCollapsed: boolean
  numberOfItems: number
  onClick: () => void
}

const ViewHideButton = ({
  isCollapsed,
  numberOfItems,
  onClick
}: IViewHideButton) => {
  if (numberOfItems <= MAX_ITEMS_IN_ROW) return null

  const classes = classnames(styles.viewHideButton, {
    [styles.viewHideButtonFor3]: numberOfItems == 3,
    [styles.viewHideButtonFor4]: numberOfItems == 4,
    [styles.viewHideButtonFor5]: numberOfItems == 5,
    [styles.viewHideButtonFor6]: numberOfItems == 6,
    [styles.viewHideButtonFor7]: numberOfItems == 7,
    [styles.viewHideButtonFor8]: numberOfItems == 8,
    [styles.viewHideButtonFor9]: numberOfItems == 9,
    [styles.viewHideButtonFor10]: numberOfItems == 10
  })

  const buttonLabel = isCollapsed ? 'Show more' : 'Show less'

  return (
    <button
      className={classes}
      onClick={() => {
        reportClick(buttonLabel)
        onClick()
      }}
      aria-expanded={!isCollapsed}
    >
      {buttonLabel + ' '}
      <span
        className={classnames(styles.chevron, {
          [styles.bottom]: isCollapsed
        })}
      />
    </button>
  )
}

const categoryNameFromPath = (path: string) =>
  (path.split('/').pop() as string).toLowerCase()

const TextCategoriesBlock = ({
  data,
  currentSite
}: ITextCategoriesBlockProps) => {
  const [isCollapsed, setIsCollapsed] = React.useState(true)

  const tabCategory = data[0]
  const tabCategoryPath = tabCategory.urlSearchParams.category

  const tabLabel =
    configs[currentSite].reduce(
      (acc: string, config: marketApi.ICategory) =>
        config.path === tabCategoryPath ? config.name : acc,
      ''
    ) || startCase(tabCategoryPath)

  const tabContent = tail(data)

  return (
    <section>
      <div className={styles.tabbedTableWrapper}>
        <div className={styles.tabsWrapper}>
          <Tabs value={0}>
            <Tab key={0} value={0}>
              <div className={styles.tabInner}>
                <span className={styles.tabbedNavLinkIcon}>
                  <SVGSpriteWrapper
                    spriteURL={`${config.assets.PUBLIC_PATH}/images/categories/sprites-${currentSite}-v3.svg`}
                  >
                    {SVGSpriteWrapper.SVG({
                      iconId: `#${currentSite}-svg-${categoryNameFromPath(
                        tabCategoryPath
                      )}`,
                      fallbackId: `#${currentSite}-svg-generic`,
                      backgroundColor: '#ffffff'
                    })}
                  </SVGSpriteWrapper>
                </span>
                <span className={styles.tabbedNavLinkTitle}>
                  {tabLabel} categories
                </span>
              </div>
            </Tab>
          </Tabs>
        </div>
        {[tabContent].map((item: any, tabIndex: number) => (
          <div
            key={tabIndex}
            className={classnames(styles.tabContent, {
              [styles.collapsed]: isCollapsed
            })}
          >
            {item.map(renderLink(styles.tabbedContentLink, chevronIcon))}
          </div>
        ))}
        <div className={styles.tabsWrapper}>
          {(tabCategory.subcategories || []).map(renderLink(styles.browseLink))}
        </div>
        <ViewHideButton
          isCollapsed={isCollapsed}
          numberOfItems={tabContent.length}
          onClick={() => setIsCollapsed(!isCollapsed)}
        />
      </div>
    </section>
  )
}

export default TextCategoriesBlock
